import { Outlet, useNavigate } from "react-router";

import { Helmet } from "react-helmet";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";

import { setPolicyErrorState } from "@covertree/new-business-redux";
import { SurplusLinesInformationModal } from "@covertree/new-business-modals";
import { Button, CloseButton, GoBackButton, LoadingFrame } from "@covertree/ui";
import { DrawerNavigation } from "@covertree/ui/navigation";

import formatPageTitle from "@utils/formatPageTitle";
import { useAppDispatch } from "@redux/hooks";

import { drawerNavigationItems } from "./drawerNavigationItems";
import { useNewPolicyLayout } from "./hooks/useNewPolicyLayout";

export function NewPolicyLayout() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    handleClose,
    isSurplusLinesModalOpen,
    policyErrorState,
    resetFields,
    rest,
    setIsSurplusLinesModalOpen,
    showBackButton,
    showLoadingFrame,
    showResetButton,
    span,
    title,
  } = useNewPolicyLayout();

  return (
    <>
      <Helmet>
        <title>{formatPageTitle(title)}</title>
      </Helmet>
      <Drawer
        anchor="bottom"
        open
        PaperProps={{
          sx: (theme) => ({ borderRadius: theme.spacing(3, 3, 0, 0) }),
        }}>
        <>
          {showResetButton && (
            <Button
              variant="outlined"
              sx={(theme) => ({
                position: "fixed",
                bottom: theme.spacing(5),
                left: theme.spacing(12),
              })}
              onClick={resetFields}>
              Reset All Fields
            </Button>
          )}

          <Container
            sx={{
              height: "90vh",
              minWidth: "100%",
            }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(15, 1fr)",
              }}>
              <Box
                position="sticky"
                top={0}
                py={5}
                sx={{
                  gridColumnStart: 2,
                  gridColumnEnd: "span 3",
                  backgroundColor: "background.default",
                  zIndex: 100,
                }}>
                <Typography variant="h3">{title}</Typography>
              </Box>

              <Box
                sx={(theme) => ({
                  background: "#fff",
                  display: "flex",
                  flexDirection: "row",
                  flexGrow: 1,
                  alignItems: "center",
                  justifyContent: showBackButton ? "space-between" : "flex-end",
                  position: "sticky",
                  top: "0",
                  gap: theme.spacing(2),
                  zIndex: 100,
                  gridColumnStart: 5,
                  gridColumnEnd: "span 10",
                })}>
                {showBackButton && (
                  <GoBackButton
                    onClick={() => {
                      if (policyErrorState) {
                        dispatch(setPolicyErrorState(undefined));
                      }

                      navigate(-1);
                    }}
                  />
                )}
                <Box display="flex" py={5}>
                  <CloseButton
                    testId="drawer-close-button"
                    onClick={handleClose}
                  />
                </Box>
              </Box>

              <DrawerNavigation items={drawerNavigationItems} pt={1} />
              <Box
                mb={3}
                sx={{
                  gridColumnStart: 5,
                  gridColumnEnd: span,
                }}>
                <Outlet />
              </Box>
            </Box>
          </Container>
        </>
        {showLoadingFrame && (
          <Box
            sx={(theme) => ({
              backgroundColor: "background.default",
              borderTopLeftRadius: theme.spacing(3),
              borderTopRightRadius: theme.spacing(3),
              position: "fixed",
              width: "100%",
              height: "90vh",
              overflow: "hidden",
              bottom: 0,
              left: 0,
              zIndex: 2000,
            })}>
            <LoadingFrame
              isFixed={false}
              image="/animations/spinner.gif"
              {...rest}
            />
          </Box>
        )}
        <SurplusLinesInformationModal
          open={isSurplusLinesModalOpen}
          onClose={() => setIsSurplusLinesModalOpen(false)}
        />
      </Drawer>
    </>
  );
}
